// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Video, 
  Save,
  X,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import UserMentionSelector from '@/components/press/UserMentionSelector';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface VideoRecord {
  id: string;
  membership_slug: string;
  title: string;
  youtube_id: string;
  description: string | null;
  thumbnail_url: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

interface Membership {
  id: string;
  slug: string;
  name: string;
}

interface Profile {
  id: string;
  name: string;
  username: string;
  avatar_url?: string;
}

const VideosManagement: React.FC = () => {
  const { user, profile } = useAuth();
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<VideoRecord | null>(null);
  const [selectedMembership, setSelectedMembership] = useState<string>('');
  const [mentionedUsers, setMentionedUsers] = useState<Profile[]>([]);
  const [formData, setFormData] = useState({
    membership_slug: '',
    title: '',
    youtube_id: '',
    description: '',
    thumbnail_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedMembership) {
      fetchVideos(selectedMembership);
    } else {
      setVideos([]);
    }
  }, [selectedMembership]);

  const fetchData = async () => {
    try {
      // Fetch video-related memberships
      const { data: membershipData, error: membershipError } = await supabase
        .from('memberships')
        .select('id, slug, name')
        .eq('is_addon', true)
        .ilike('slug', 'video_%')
        .order('name');

      if (membershipError) throw membershipError;
      setMemberships(membershipData || []);

      // Set first membership as default if available
      if (membershipData && membershipData.length > 0) {
        setSelectedMembership(membershipData[0].slug);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async (membershipSlug: string) => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('membership_slug', membershipSlug)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setVideos(data || []);
    } catch (error: any) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    }
  };

  const extractYouTubeId = (url: string): string => {
    // Extract YouTube ID from various URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return url; // Return as-is if no pattern matches
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedMembership) {
      toast.error('Please select a membership');
      return;
    }

    if (!user?.id) {
      toast.error('Authentication required');
      return;
    }

    try {
      const youtubeId = extractYouTubeId(formData.youtube_id);
      
      const videoData = {
        membership_slug: selectedMembership,
        title: formData.title,
        youtube_id: youtubeId,
        description: formData.description || null,
        thumbnail_url: formData.thumbnail_url || null,
        is_active: formData.is_active,
        sort_order: editingVideo ? editingVideo.sort_order : videos.length + 1
      };

      let savedVideoId = editingVideo?.id;

      if (editingVideo) {
        const { error } = await supabase
          .from('videos')
          .update(videoData)
          .eq('id', editingVideo.id);

        if (error) throw error;
        toast.success('Video updated successfully');
      } else {
        const { data: insertedData, error } = await supabase
          .from('videos')
          .insert([videoData])
          .select('id')
          .single();

        if (error) throw error;
        savedVideoId = insertedData.id;
        toast.success('Video created successfully');
      }

      // Save mentions if we have any and the video was saved successfully
      if (savedVideoId && mentionedUsers.length > 0) {
        console.log('💾 Saving mentions for video:', savedVideoId);
        console.log('👥 Mentioned users:', mentionedUsers.map(u => ({ id: u.id, name: u.name })));
        
        // Use custom video mention save logic since the hook is for articles
        await saveVideoMentions(savedVideoId, mentionedUsers);
      }

      setShowForm(false);
      setEditingVideo(null);
      resetForm();
      setMentionedUsers([]);
      fetchVideos(selectedMembership);
    } catch (error: any) {
      console.error('Error saving video:', error);
      toast.error('Failed to save video');
    }
  };

  const saveVideoMentions = async (videoId: string, mentionedUsers: Profile[]) => {
    try {
      if (mentionedUsers.length === 0) {
        return true;
      }

      // Get current user's profile ID
      const { data: currentProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();

      if (profileError || !currentProfile) {
        console.error('❌ No profile found for current user:', profileError);
        return false;
      }

      // Check for existing mentions
      const { data: existingMentions, error: fetchError } = await supabase
        .from('profile_mentions')
        .select('profile_id')
        .eq('source_type', 'video')
        .eq('source_id', videoId);

      if (fetchError) {
        console.error('❌ Error fetching existing mentions:', fetchError);
        return false;
      }

      const existingProfileIds = existingMentions?.map(m => m.profile_id) || [];
      const newMentions = mentionedUsers.filter(user => !existingProfileIds.includes(user.id));

      if (newMentions.length === 0) {
        return true;
      }

      // Insert new mentions with approved status (video mentions don't need approval)
      const mentionsToInsert = newMentions.map(mentionedUser => ({
        profile_id: mentionedUser.id,
        source_type: 'video' as const,
        source_id: videoId,
        mentioned_by: currentProfile.id,
        status: 'approved' as const,
        approved_at: new Date().toISOString()
      }));

      const { data: insertedMentions, error: insertError } = await supabase
        .from('profile_mentions')
        .insert(mentionsToInsert)
        .select('id, profile_id');

      if (insertError) {
        console.error('❌ Error inserting mentions:', insertError);
        return false;
      }

      console.log('✅ Successfully saved video mentions');

      // Trigger notifications for each mention
      if (insertedMentions) {
        for (const mention of insertedMentions) {
          try {
            const { error: notificationError } = await invokeEdgeFunction('send-mention-notification', {
              body: {
                mentionId: mention.id,
                videoId: videoId,
                mentionedProfileId: mention.profile_id,
                sourceType: 'video'
              }
            });

            if (notificationError) {
              console.error('❌ Error sending notification:', notificationError);
            }
          } catch (notificationError) {
            console.error('❌ Exception sending notification:', notificationError);
          }
        }
      }

      toast.success(`Video saved with ${mentionedUsers.length} athlete mentions`);
      return true;
    } catch (error) {
      console.error('❌ Exception saving video mentions:', error);
      return false;
    }
  };

  const handleEdit = (video: VideoRecord) => {
    setEditingVideo(video);
    setFormData({
      membership_slug: video.membership_slug,
      title: video.title,
      youtube_id: video.youtube_id,
      description: video.description || '',
      thumbnail_url: video.thumbnail_url || '',
      is_active: video.is_active
    });
    // Reset mentioned users when editing
    setMentionedUsers([]);
    setShowForm(true);
  };

  const handleDelete = async (videoId: string) => {
    if (!confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId);

      if (error) throw error;
      toast.success('Video deleted successfully');
      fetchVideos(selectedMembership);
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const updateSortOrder = async (videoId: string, newSortOrder: number) => {
    try {
      const { error } = await supabase
        .from('videos')
        .update({ sort_order: newSortOrder })
        .eq('id', videoId);

      if (error) throw error;
      fetchVideos(selectedMembership);
    } catch (error: any) {
      console.error('Error updating sort order:', error);
      toast.error('Failed to update sort order');
    }
  };

  const moveVideo = (video: VideoRecord, direction: 'up' | 'down') => {
    const currentIndex = videos.findIndex(v => v.id === video.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= videos.length) return;

    const targetVideo = videos[targetIndex];
    
    // Swap sort orders
    updateSortOrder(video.id, targetVideo.sort_order);
    updateSortOrder(targetVideo.id, video.sort_order);
  };

  const toggleActiveStatus = async (videoId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('videos')
        .update({ is_active: !currentStatus })
        .eq('id', videoId);

      if (error) throw error;
      toast.success(`Video ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchVideos(selectedMembership);
    } catch (error: any) {
      console.error('Error updating video status:', error);
      toast.error('Failed to update video status');
    }
  };

  const resetForm = () => {
    setFormData({
      membership_slug: '',
      title: '',
      youtube_id: '',
      description: '',
      thumbnail_url: '',
      is_active: true
    });
    setMentionedUsers([]);
  };

  const handleNewVideo = () => {
    resetForm();
    setEditingVideo(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingVideo(null);
    resetForm();
  };

  const getYouTubeThumbnail = (youtubeId: string) => {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Videos Management</h1>
          <p className="text-muted-foreground">Manage video content for each membership</p>
        </div>
        
        {!showForm && selectedMembership && (
          <Button onClick={handleNewVideo}>
            <Plus className="h-4 w-4 mr-2" />
            Add Video
          </Button>
        )}
      </div>

      {/* Membership Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Membership</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedMembership} onValueChange={setSelectedMembership}>
            <SelectTrigger className="w-full md:w-96">
              <SelectValue placeholder="Select a video membership" />
            </SelectTrigger>
            <SelectContent>
              {memberships.map((membership) => (
                <SelectItem key={membership.id} value={membership.slug}>
                  {membership.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Inline Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {editingVideo ? 'Edit Video' : 'Add New Video'}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="title">Video Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="youtube_id">YouTube URL or ID *</Label>
                  <Input
                    id="youtube_id"
                    value={formData.youtube_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, youtube_id: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=... or video ID"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="thumbnail_url">Custom Thumbnail URL</Label>
                  <Input
                    id="thumbnail_url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                    placeholder="Optional - will use YouTube thumbnail if empty"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Active (visible to users)</Label>
                </div>
              </div>

              {/* Athlete Mentions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t">
                <UserMentionSelector
                  selectedUsers={mentionedUsers}
                  onUsersChange={setMentionedUsers}
                />
                
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  {editingVideo ? 'Update Video' : 'Create Video'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Videos List */}
      {selectedMembership && (
        <>
          {videos.length === 0 && !showForm ? (
            <Card>
              <CardContent className="text-center py-12">
                <Video className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Videos Found</h3>
                <p className="text-muted-foreground mb-6">Get started by adding your first video.</p>
                <Button onClick={handleNewVideo}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Video
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {videos.map((video, index) => (
                <Card key={video.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {video.title}
                          <Badge variant={video.is_active ? 'default' : 'secondary'}>
                            {video.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </CardTitle>
                        {video.description && (
                          <p className="text-muted-foreground mt-2">{video.description}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveVideo(video, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveVideo(video, 'down')}
                            disabled={index === videos.length - 1}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActiveStatus(video.id, video.is_active)}
                        >
                          {video.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(video)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(video.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="flex gap-4">
                      {/* Video Thumbnail */}
                      <div className="flex-shrink-0">
                        <img
                          src={video.thumbnail_url || getYouTubeThumbnail(video.youtube_id)}
                          alt={video.title}
                          className="w-32 h-18 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.src = getYouTubeThumbnail(video.youtube_id);
                          }}
                        />
                      </div>
                      
                      {/* Video Details */}
                      <div className="flex-1">
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>YouTube ID: <code className="bg-muted px-1 rounded">{video.youtube_id}</code></p>
                          <p>Sort Order: {video.sort_order}</p>
                          <p>Created: {new Date(video.created_at).toLocaleDateString()}</p>
                        </div>
                        
                        <div className="mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`https://www.youtube.com/watch?v=${video.youtube_id}`, '_blank')}
                          >
                            View on YouTube
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideosManagement;